import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Group,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AnalyticsPoolChatStat } from "../../../api.ts";
import { adminErrorMessage, adminGet, adminSend } from "../../../api.ts";
import { notifyAdminError, notifyAdminSuccess } from "../../../lib/admin-notify.ts";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { TablePagination } from "../../../components/TablePagination.tsx";
import { useClientPagination } from "../../../hooks/useClientPagination.ts";

type HealthFilter = "all" | "empty" | "exhausted" | "ok";

const healthOf = (item: AnalyticsPoolChatStat): "empty" | "exhausted" | "ok" => {
  const total = item.available + item.assigned;
  if (total === 0) return "empty";
  if (item.available === 0) return "exhausted";
  return "ok";
};

interface PoolResponse {
  readonly items: AnalyticsPoolChatStat[];
  readonly totalAvailable: number;
  readonly totalAssigned: number;
}

interface RefillResponse {
  readonly chats: number;
  readonly created: number;
}

export function LinkPoolTab(props: { readonly botId: string }) {
  const { botId } = props;
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState<HealthFilter>("all");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["bot", botId, "analytics-pool"],
    queryFn: () => adminGet<PoolResponse>(`/analytics/${botId}/pool`),
    refetchInterval: 30_000,
  });

  const refill = useMutation({
    mutationFn: () => adminSend<RefillResponse>("POST", `/analytics/${botId}/pool/refill`),
    onSuccess: (res) => {
      notifyAdminSuccess(
        res.created > 0
          ? `已补充 ${res.created} 条链接（共检查 ${res.chats} 个 chat）`
          : `链接池已充足，无需补充（共检查 ${res.chats} 个 chat，可用链接已达目标水位）`,
      );
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-pool"] });
    },
    onError: (e) => notifyAdminError(e, "补充失败"),
  });

  const allItems = data?.items ?? [];
  const keyword = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    return allItems.filter((item) => {
      if (healthFilter !== "all" && healthOf(item) !== healthFilter) return false;
      if (keyword === "") return true;
      const title = (item.title ?? "").toLowerCase();
      const chatId = String(item.chatId);
      return title.includes(keyword) || chatId.includes(keyword);
    });
  }, [allItems, healthFilter, keyword]);
  const resetKey = `${healthFilter}|${keyword}`;
  const { pageItems, page, setPage, pageSize, total, totalPages } = useClientPagination(
    filtered,
    resetKey,
  );

  const rows = pageItems.map((item) => {
    const poolTotal = item.available + item.assigned;
    const health = healthOf(item);
    const healthColor =
      health === "empty"
        ? "gray"
        : health === "exhausted"
          ? "red"
          : item.available / poolTotal > 0.5
            ? "green"
            : item.available / poolTotal > 0.1
              ? "yellow"
              : "red";

    return (
      <Table.Tr key={item.chatId}>
        <Table.Td>{item.title ?? item.chatId}</Table.Td>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {item.chatId}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge color="green" size="sm">
            {item.available}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge color="orange" size="sm">
            {item.assigned}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge color={healthColor} size="sm">
            {health === "empty" ? "空池" : health === "exhausted" ? "已耗尽" : "正常"}
          </Badge>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
        {isLoading ? (
          <>
            <Skeleton h={80} radius="md" />
            <Skeleton h={80} radius="md" />
          </>
        ) : (
          <>
            <Card shadow="xs" padding="md" radius="md" withBorder>
              <Text size="xs" c="dimmed" mb={4}>
                总可用链接
              </Text>
              <Title order={3}>{(data?.totalAvailable ?? 0).toLocaleString()}</Title>
            </Card>
            <Card shadow="xs" padding="md" radius="md" withBorder>
              <Text size="xs" c="dimmed" mb={4}>
                当前占用链接
              </Text>
              <Title order={3}>{(data?.totalAssigned ?? 0).toLocaleString()}</Title>
            </Card>
          </>
        )}
      </SimpleGrid>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            各 Chat 池状态
          </Text>
          <Button
            size="sm"
            variant="light"
            loading={refill.isPending}
            onClick={() => refill.mutate()}
          >
            立即预热
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          补池 cron 每 2 分钟自动补链。耗尽时中转页降级 fallback。首次启用可点「立即预热」。
        </Text>
        {isLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} h={40} radius="sm" />
            ))}
          </Stack>
        ) : isError ? (
          <Text c="red" size="sm" ta="center" py="lg">
            {adminErrorMessage(error)}
          </Text>
        ) : allItems.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="xl">
            暂无池数据，请先添加频道/群聊并等待 cron 补池。
          </Text>
        ) : (
          <>
            <Group gap="sm" mb="sm" wrap="wrap">
              <Select
                size="xs"
                label="健康"
                data={[
                  { label: "全部", value: "all" },
                  { label: "空池", value: "empty" },
                  { label: "已耗尽", value: "exhausted" },
                  { label: "正常", value: "ok" },
                ]}
                value={healthFilter}
                onChange={(v) => setHealthFilter((v ?? "all") as HealthFilter)}
                style={{ width: 130 }}
                clearable={false}
              />
              <TextInput
                size="xs"
                label="搜索"
                placeholder="Chat 名称 / Chat ID"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{ minWidth: 220, flex: 1 }}
              />
            </Group>
            <AdminTable minWidth={600}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Chat 名称</Table.Th>
                  <Table.Th>Chat ID</Table.Th>
                  <Table.Th>可用链接</Table.Th>
                  <Table.Th>已占用</Table.Th>
                  <Table.Th>健康状态</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" size="sm" ta="center">
                        无匹配记录
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  rows
                )}
              </Table.Tbody>
            </AdminTable>
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </Stack>
  );
}
